'use client'

import React from 'react'

import { useExtractSpaceProps } from '../../hooks'
import { SpaceProps } from '../../themes'
import { Field } from '../Field'
import {
  SearchableSelectInput,
  SearchableSelectInputProps,
} from './SearchableSelectInput'

export { normalizeForSearch } from './normalize'
export type { SearchableOption } from './SearchableSelectInput'

export interface SearchableSelectFieldProps
  extends Omit<SearchableSelectInputProps, 'hasError'>,
    SpaceProps {
  className?: string
  label?: string
  required?: boolean
  errorIcon?: React.ReactNode
  errorMessages?: string | string[]
  captionIcon?: React.ReactNode
  captionMessages?: string | string[]
  disableBodyScrollLock?: boolean
}

const SearchableSelectFieldInner = (
  {
    id,
    className,
    label,
    required,
    autoWidth,
    placement = 'bottom',
    errorIcon,
    errorMessages,
    captionIcon,
    captionMessages,
    ...props
  }: SearchableSelectFieldProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const autoId = React.useId()
  const inputId = id ?? autoId
  const [spaceProps, selectProps] = useExtractSpaceProps(props)

  return (
    <Field className={className} {...spaceProps}>
      {label && (
        <Field.Label id={inputId} required={required}>
          {label}
        </Field.Label>
      )}

      <Field.Content>
        <SearchableSelectInput
          id={inputId}
          hasError={!!errorMessages}
          ref={ref}
          autoWidth={autoWidth}
          placement={placement}
          {...selectProps}
        />
      </Field.Content>

      <Field.Messages
        mt={2}
        type="error"
        messages={errorMessages}
        icon={errorIcon}
      />

      <Field.Messages
        mt={errorMessages ? 1 : 2}
        type="caption"
        messages={captionMessages}
        icon={captionIcon}
      />
    </Field>
  )
}

export const SearchableSelectField = React.forwardRef(
  SearchableSelectFieldInner,
)
